import { cloneDeep } from 'lodash';
import { TipModeEnum } from '@qlub-dev/qlub-kit/dist/components/Tip/types';
import CommonAPIManager from '../common';
import {
    BillOnLandingEnum,
    BillPageDescriptionModeEnum,
    CheckEnum,
    convertMapType,
    EnableEnum,
    MenuPageDescriptionModeEnum,
    ODFTypeEnum,
    OrderSummaryMode,
    ReviewServiceTypeEnum,
    SplashConfigEnum,
    SplitModeEnum,
    SplitModeTypeEnum,
    SplitUIVersion,
    TipBoxPresentationEnum,
    TipGamificationModeEnum,
    TipLabelTypeEnum,
    TipModalEventConditionEnum,
    TipModalTriggerPointConditionEnum,
    TipUIv2Enum,
    VersionEnum,
    VisibilityEnum,
    SmartTipModalEventConditionEnum,
    SmartTipModalTriggerPointConditionEnum,
    ConfirmationPageBrandingLayoutEnum,
} from '../vendor';
import { checkBrandCondition } from '../../utility/vendor';

export enum IMenuPageMode {
    Old = 'old',
    New = 'new',
}

export enum SplitRealTimeFeeCalcModeEnum {
    Enabled = 'enabled',
    Disabled = 'disabled',
}

export interface ISplashConfig {
    splashEnable?: boolean;
    splashTitle?: string;
    splashDesc?: string;
    splashTheme?: SplashConfigEnum;
    splashHideTypography?: boolean;
}

export interface IConfig extends ISplashConfig {
    // Display Options
    showTip: boolean;
    showTax: boolean;
    showVat: boolean;
    showGst: boolean;
    showDiscount: boolean;
    showArea: boolean;
    showAddressLine2: boolean;
    compactInvoiceTable: boolean;
    showOrderSubtotal: boolean;
    showTransactionTime?: boolean;

    // UI Configuration
    defaultLang: string;
    hideCountryFlagsFromLanguageSelector?: EnableEnum;
    termsAndConditions?: string;
    apiVersion: string;
    newAppHost: string;
    splitMode: SplitModeEnum;
    splitOptions: SplitModeTypeEnum[];
    splitUIVersion?: SplitUIVersion;
    splitRealTimeFeeCalculation?: SplitRealTimeFeeCalcModeEnum;
    splitByItemV2Ui?: EnableEnum;
    splitByShareAlgorithm?: VersionEnum;

    // Tip Configuration
    tipOptions: number[];
    tipMode: TipModeEnum;
    tipDefault?: number;
    tipGamificationMode?: TipGamificationModeEnum;
    tipUIv2?: TipUIv2Enum;
    tipBoxPresentation?: TipBoxPresentationEnum;
    tipLabelType?: TipLabelTypeEnum;
    tipCustomHeader?: string;
    tipCustomDescription?: string;
    tipModalTriggerCondition?: TipModalTriggerPointConditionEnum;
    tipModalEventCondition?: TipModalEventConditionEnum;
    tipDefaultForTipModal?: number;
    tipModalHideNotNow?: boolean;
    tipShowZeroValue?: boolean;
    hideTipAmountTipBox?: boolean;
    tipModalWithPayment?: boolean;
    tipRoundingCRV?: number;
    tipRoundingMTC?: number;
    mostCommonTip?: number;
    tipReminderContent?: string;
    smartTip?: EnableEnum;
    smartTipStepSizesJSONURL?: string;
    smartTipModalEventCondition?: SmartTipModalEventConditionEnum;
    smartTipModalTriggerCondition?: SmartTipModalTriggerPointConditionEnum;

    // Feature Toggles
    hideDownloadInvoice: boolean;
    hideEmailInvoice: boolean;
    hideRate: boolean;
    newEmailServer?: boolean;
    emailTermsAndConditionsV4?: boolean;
    enableBrandingInConfirmationPage?: ConfirmationPageBrandingLayoutEnum;
    changePayNowButtonTextForSG?: EnableEnum;
    voucherHasActiveCampaign?: boolean;
    authPreventInitialGuest?: boolean;
    reviewEnabled?: EnableEnum;
    enableSSOLogin?: EnableEnum;
    commissionOptionalDinerFee?: EnableEnum;
    downloadReceiptEnable?: EnableEnum;
    languageSwitcherUIv2?: EnableEnum;
    reviewFlowAfterReceiptEmail?: EnableEnum;

    // Conditional Configurations
    apiCondition: 'default' | 'new' | 'new_by_vendor';
    apiConditionVendors: string[];
    newAppCondition: 'default' | 'new' | 'new_by_vendor';
    newAppConditionVendors: string[];
    customerAppBlackMode?: string[];
    displayCodeBoxCondition?: 'all' | 'byBrand';
    displayCodeBoxBrands?: string[];
    enableUserProfile?: 'enable' | 'disable';
    enableUserProfileByBrand?: string[];

    // Text Content
    dinerFeeTooltipText?: string;
    showDinerFeeTooltip?: string;
    inclusiveChargesTextWithTip?: string;
    inclusiveChargesText?: string;
    paymentLinkCustomerCommissionAlias?: string;
    confirmationPageFooterMessageText?: string;
    confirmationPageFooterMessageLink?: string;
    orderStatusTitle?: { [key: string]: string };
    orderStatusDesc?: { [key: string]: string };
    landingFooterDisclaimerText?: string;

    // Page Specific
    billPageDescriptionMode?: BillPageDescriptionModeEnum;
    menuPageDescriptionMode?: MenuPageDescriptionModeEnum;
    menuPageDescription?: string;
    menuPageMode?: IMenuPageMode;
    orderSummary?: OrderSummaryMode;

    // User Profile & Communication
    contactUsWhatsAppNumber?: string;
    paymentReceipts?: VisibilityEnum;
    communicationConsentVisibility?: VisibilityEnum;
    communicationConsentMessage?: string;
    communicationConsentDefault?: CheckEnum;

    // Review System
    reviewMode?: ReviewServiceTypeEnum;
    defaultRate?: number;
    goodRatingThreshold?: number;
    showGoogleReviewRedirectionFlow?: EnableEnum;
    enableGoogleReviewAutoRedirect?: EnableEnum;
    collectCustomerEmail?: boolean;
    collectCustomerMobileNumber?: boolean;
    customerDataCollectionThreshold?: number;

    // Confirmation Page
    confirmationPageFooterMessageVisibility?: EnableEnum;
    emailReceiptVersion?: VersionEnum;

    // Optional Diner Fee
    commissionOptionalDinerFeeDefault?: CheckEnum;
    commissionOptionalDinerFeeTooltip?: string;
    commissionOptionalDinerFeeLabel?: string;
    commissionOptionalDinerFeeUiType?: ODFTypeEnum;

    // Bill on landing
    bolVariant?: BillOnLandingEnum;
    bolCaption?: string;
    bolTimeout?: number;
}

const defaultConfig: IConfig = {
    showTip: true,
    showTax: false,
    showVat: false,
    showGst: false,
    showDiscount: false,
    showArea: true,
    showAddressLine2: true,
    compactInvoiceTable: false,
    showOrderSubtotal: false,
    defaultLang: 'en',
    tipOptions: [5, 10, 15, -1],
    tipMode: TipModeEnum.Percentage,
    hideDownloadInvoice: false,
    hideEmailInvoice: false,
    hideRate: false,
    splitMode: SplitModeEnum.Legacy,
    splitOptions: [SplitModeTypeEnum.byItem, SplitModeTypeEnum.byShare, SplitModeTypeEnum.custom],
    apiCondition: 'default',
    apiVersion: '1',
    apiConditionVendors: [],
    newAppCondition: 'default',
    newAppConditionVendors: [],
    newAppHost: 'newapp.qlub.io',
    emailTermsAndConditionsV4: false,
    tipGamificationMode: TipGamificationModeEnum.GamificationOff,
    tipUIv2: TipUIv2Enum.Disable,
};

export const isUserProfileEnable = (
    countryConfig?: IConfig | null,
    enableUserProfileOnVendor?: EnableEnum,
    brandId?: string,
): boolean => {
    if (!countryConfig) return false;
    if (enableUserProfileOnVendor === 'enable') return true;
    if (!enableUserProfileOnVendor && brandId && countryConfig.enableUserProfileByBrand?.length) {
        return checkBrandCondition(countryConfig.enableUserProfileByBrand, brandId);
    }
    return false;
};

export default class ConfigRepo {
    /* eslint-disable */
    public static getInstance(): ConfigRepo | undefined {
        // @ts-ignore
        if (!process.browser) {
            return;
        }
        if (!this.instance) {
            this.instance = new ConfigRepo();
        }

        return this.instance;
    }

    /* eslint-enable */

    private static instance: ConfigRepo;

    private config: Record<string, IConfig> = {};

    private fetchedConfig: Record<string, boolean> = {};

    private promises: Array<{ resolve: any; reject: any }> = [];

    private generalApi = CommonAPIManager.getInstance();

    private constructor() {
        this.initializeConfigs();
    }

    private initializeConfigs() {
        const countrySpecific: Record<string, Partial<IConfig>> = {
            jp: {
                showTax: true,
                showArea: false,
                showAddressLine2: false,
                compactInvoiceTable: true,
                defaultLang: 'ja',
            },
            br: { defaultLang: 'pt' },
            au: { showGst: true },
        };

        this.config = {
            default: { ...defaultConfig },
            ae: { ...defaultConfig },
            in: { ...defaultConfig },
            jp: { ...defaultConfig, ...countrySpecific.jp },
            br: { ...defaultConfig, ...countrySpecific.br },
            au: { ...defaultConfig, ...countrySpecific.au },
        };
    }

    public async getConfig(cc: string): Promise<IConfig> {
        if (!cc) {
            return cloneDeep(this.config.default);
        }

        if (this.config[cc] && this.fetchedConfig[cc]) {
            return this.getConfigByLocale(cc);
        }

        await this.init(cc);
        return this.getConfigByLocale(cc);
    }

    private getConfigByLocale(cc: string): IConfig {
        return cloneDeep(this.config[cc] || this.config.default);
    }

    private init(cc: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.promises.push({ resolve, reject });
            if (this.promises.length === 1) {
                this.getRemoteConfig(cc)
                    ?.then(() => {
                        this.promises.forEach((p) => p.resolve());
                    })
                    .catch(() => {
                        this.promises.forEach((p) => p.reject());
                    })
                    .finally(() => {
                        this.promises = [];
                    });
            }
        });
    }

    private async getRemoteConfig(cc: string): Promise<any> {
        const res = await this.generalApi.getCountry(cc);
        this.config[cc] = this.parseConfig(cc, res);
        this.fetchedConfig[cc] = true;
        return res;
    }

    private parseConfig(cc: string, config: Record<string, any>): IConfig {
        const cfg: IConfig = cloneDeep(this.config[cc] || this.config.default);

        const specialCases: Record<string, (val: any) => any> = {
            tipOptions: (val) => (val?.length ? val : cfg.tipOptions),
            splitOptions: (val) => val || cfg.splitOptions,
            apiConditionVendors: (val) => (Array.isArray(val) ? val : []),
            newAppConditionVendors: (val) => (Array.isArray(val) ? val : []),
            customerAppBlackMode: (val) => (Array.isArray(val) ? val : []),
            displayCodeBoxBrands: (val) => (Array.isArray(val) ? val : []),
            enableUserProfileByBrand: (val) => (Array.isArray(val) ? val : []),
            orderStatusTitle: (val) => (val ? convertMapType(val) : undefined),
            orderStatusDesc: (val) => (val ? convertMapType(val) : undefined),
            tipRoundingCRV: (val) => (val ? parseFloat(val) : 0),
            tipRoundingMTC: (val) => (val ? parseFloat(val) : 0),
        };

        return Object.entries(config).reduce<any>((acc, [key, val]) => {
            if (key in specialCases) {
                acc[key] = specialCases[key](val);
            } else {
                acc[key] = val;
            }
            return acc;
        }, cfg as any);
    }
}
